import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, AssistiveDevice } from '../api';
import { assistiveDeviceEndpoints, endpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import { INPUT } from './event/shared';

/**
 * Lending a receiver, and getting it back (item 250 — D-317).
 *
 * The screen answers one question and puts it first: **what is still out**. A
 * list of devices is reference; a list of things somebody has not brought back
 * is the reason anybody opens this at eleven at night.
 *
 * A device that is out shows who has it, on the row, so the window can say a
 * name rather than reading a serial number back to somebody.
 */

const CONDITIONS = ['GOOD', 'NEEDS_BATTERY', 'BROKEN', 'LOST'] as const;
const readable = (code: string) => code.replaceAll('_', ' ').toLowerCase();

export default function AssistiveDevicesPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [venueId, setVenueId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const venues = useQuery({ queryKey: ['venues'], queryFn: endpoints.venues });
  const devices = useQuery({ queryKey: ['assistive-devices', venueId], queryFn: () => assistiveDeviceEndpoints.forVenue(venueId), enabled: Boolean(venueId) });
  const outstanding = useQuery({
    queryKey: ['assistive-devices', 'outstanding', venueId],
    queryFn: () => assistiveDeviceEndpoints.outstanding({ venue_id: venueId }),
    enabled: Boolean(venueId),
    // Read while a door is running, so it cannot be a page somebody has to reload.
    refetchInterval: 30_000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['assistive-devices'] });
  const onError = (cause: ApiError) => setError(cause.message);
  const onDone = () => {
    setError(null);
    void refresh();
  };

  const lend = useMutation({ mutationFn: assistiveDeviceEndpoints.lend, onSuccess: onDone, onError });
  const takeBack = useMutation({
    mutationFn: ({ loanId, condition }: { loanId: string; condition: string }) => assistiveDeviceEndpoints.takeBack(loanId, { condition }),
    onSuccess: onDone,
    onError,
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) => assistiveDeviceEndpoints.update(id, input),
    onSuccess: onDone,
    onError,
  });
  const create = useMutation({
    mutationFn: (input: { asset_tag: string; feature_code: string; model?: string | null }) => assistiveDeviceEndpoints.add(venueId, input),
    onSuccess: () => {
      setAdding(false);
      onDone();
    },
    onError,
  });

  if (!can('events.view')) return <NotPermitted permission="events.view" />;
  const lendable = can('assistive_devices.lend');
  const manageable = can('assistive_devices.manage');
  const rows = devices.data?.devices ?? [];
  const loans = outstanding.data?.loans ?? [];

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <Panel
        title="Assistive listening devices"
        description="Receivers and headsets lent at the window. The event page has promised these since the venue said it offers them; this is the drawer they come out of."
      >
        <div className="flex flex-wrap items-end gap-3 px-5 py-4">
          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Venue</span>
            <select className={`mt-1 ${INPUT}`} value={venueId} onChange={(event) => setVenueId(event.target.value)}>
              <option value="">Choose a venue</option>
              {(venues.data?.venues ?? []).map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}
                </option>
              ))}
            </select>
          </label>
          {manageable && venueId && !adding ? (
            <Button className="ml-auto" variant="primary" onClick={() => setAdding(true)}>
              Add a device
            </Button>
          ) : null}
        </div>
      </Panel>

      {adding ? <AddDevice onAdd={(input) => create.mutate(input)} onCancel={() => setAdding(false)} busy={create.isPending} /> : null}

      {/*
        First, because it is the reason anybody opens this page at eleven at
        night. A list of devices is reference; a list of things somebody has not
        brought back is the question.
      */}
      {venueId ? (
        <Panel
          title={loans.length === 0 ? 'Everything is back' : `${loans.length} still out`}
          description={loans.some((loan) => loan.overdue) ? 'Anything marked overdue has been out long enough that it has probably left the building.' : 'Refreshed while the door is running.'}
        >
          {loans.length === 0 ? (
            <p className="px-5 py-4 text-sm text-ink-600">No device is out on loan.</p>
          ) : (
            <Table headers={['Device', 'Who has it', 'Held', 'Out for', '']}>
              {loans.map((loan) => (
                <tr key={loan.id} className="border-b border-line last:border-0">
                  <td className="px-5 py-3 text-sm font-medium text-ink-900">{loan.asset_tag}</td>
                  <td className="px-5 py-3 text-sm text-ink-900">{loan.borrower_name}</td>
                  <td className="px-5 py-3 text-xs text-ink-500">{loan.security_held ?? 'nothing'}</td>
                  <td className="px-5 py-3">
                    <Badge tone={loan.overdue ? 'bad' : 'neutral'}>{loan.hours_out} h</Badge>
                    <div className="mt-0.5 text-xs text-ink-400">since {formatWhen(loan.lent_at)}</div>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {lendable ? (
                      <>
                        <Button disabled={takeBack.isPending} onClick={() => takeBack.mutate({ loanId: loan.id, condition: 'GOOD' })}>
                          Back, fine
                        </Button>
                        <Button
                          variant="danger"
                          className="ml-2"
                          disabled={takeBack.isPending}
                          onClick={() => {
                            // Two buttons rather than a dropdown: at a window,
                            // "it came back" and "it did not" are the only two
                            // that need to be one press.
                            const condition = window.prompt('What state is it in? GOOD, NEEDS_BATTERY, BROKEN or LOST', 'LOST');
                            if (condition && (CONDITIONS as readonly string[]).includes(condition)) takeBack.mutate({ loanId: loan.id, condition });
                          }}
                        >
                          Something else
                        </Button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      ) : null}

      {venueId ? (
        <Panel title="The drawer" description="Everything this venue owns. A device that is out says who has it.">
          <Table headers={['Tag', 'For', 'Condition', 'Where it is', '']}>
            {devices.isPending ? (
              <EmptyRow columns={5}>Loading…</EmptyRow>
            ) : rows.length === 0 ? (
              <EmptyRow columns={5}>No devices recorded for this venue yet.</EmptyRow>
            ) : (
              rows.map((device) => <DeviceRow key={device.id} device={device} lendable={lendable} manageable={manageable} onLend={(name, security) => lend.mutate({ assistive_device_id: device.id, borrower_name: name, security_held: security })} onRetire={() => update.mutate({ id: device.id, input: { retired: true } })} onCondition={(condition) => update.mutate({ id: device.id, input: { condition } })} busy={lend.isPending || update.isPending} />)
            )}
          </Table>
        </Panel>
      ) : null}
    </div>
  );
}

function DeviceRow({
  device,
  lendable,
  manageable,
  onLend,
  onRetire,
  onCondition,
  busy,
}: {
  device: AssistiveDevice;
  lendable: boolean;
  manageable: boolean;
  onLend: (name: string, security: string | null) => void;
  onRetire: () => void;
  onCondition: (condition: string) => void;
  busy: boolean;
}) {
  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-5 py-3">
        <div className="font-medium text-ink-900">{device.asset_tag}</div>
        {device.model ? <div className="text-xs text-ink-400">{device.model}</div> : null}
      </td>
      <td className="px-5 py-3 text-sm text-ink-600">{device.feature_name ?? readable(device.feature_code)}</td>
      <td className="px-5 py-3">
        <Badge tone={device.condition === 'GOOD' ? 'ok' : device.condition === 'NEEDS_BATTERY' ? 'warn' : 'bad'}>{readable(device.condition)}</Badge>
      </td>
      <td className="px-5 py-3 text-sm">
        {device.retired_at ? (
          <span className="text-xs text-ink-400">retired</span>
        ) : device.out_on_loan ? (
          <span className="text-ink-900">
            with {device.out_on_loan.borrower_name}
            <span className="block text-xs text-ink-400">since {formatWhen(device.out_on_loan.lent_at)}</span>
          </span>
        ) : (
          <span className="text-xs text-ink-500">in the drawer</span>
        )}
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        {lendable && !device.out_on_loan && !device.retired_at && device.lendable === 1 ? (
          <Button
            disabled={busy}
            onClick={() => {
              const name = window.prompt('Who is taking it?');
              if (!name) return;
              const security = window.prompt('What are you holding? (a photo ID, or leave blank)') || null;
              onLend(name, security);
            }}
          >
            Lend it
          </Button>
        ) : null}
        {manageable && device.condition !== 'GOOD' && !device.out_on_loan && !device.retired_at ? (
          <Button className="ml-2" disabled={busy} onClick={() => onCondition('GOOD')}>
            Fixed
          </Button>
        ) : null}
        {manageable && !device.retired_at ? (
          <Button variant="danger" className="ml-2" disabled={busy} onClick={onRetire}>
            Retire
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

function AddDevice({ onAdd, onCancel, busy }: { onAdd: (input: { asset_tag: string; feature_code: string; model?: string | null }) => void; onCancel: () => void; busy: boolean }) {
  const [tag, setTag] = useState('');
  const [feature, setFeature] = useState('HEARING_ASSISTANCE');
  const [model, setModel] = useState('');
  return (
    <Panel title="A new device" description="The tag is what staff read out loud, so it has to be what is written on the device.">
      <form
        className="flex flex-wrap items-end gap-4 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          onAdd({ asset_tag: tag.trim(), feature_code: feature, model: model.trim() || null });
        }}
      >
        <label className="text-sm">
          <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Tag</span>
          <input value={tag} onChange={(event) => setTag(event.target.value)} placeholder="R-07" className={`mt-1 ${INPUT} w-28`} />
        </label>
        <label className="text-sm">
          <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">For</span>
          <select value={feature} onChange={(event) => setFeature(event.target.value)} className={`mt-1 ${INPUT}`}>
            <option value="HEARING_ASSISTANCE">Assistive listening</option>
            <option value="AUDIO_DESCRIPTION">Audio description</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Model</span>
          <input value={model} onChange={(event) => setModel(event.target.value)} placeholder="Listen LR-4200" className={`mt-1 ${INPUT} w-48`} />
        </label>
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={tag.trim().length === 0 || busy}>
            Add it
          </Button>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}
